import AutoCompleteChip from '@components/standardAutoCompleteSearch/AutoCompleteChip'
import StandardAutoCompleteSearch, {
  AutoCompleteOption,
  AutoCompleteRenderChipProps,
  AutoCompleteResultsZIndexPlacement,
} from '@components/standardAutoCompleteSearch/StandardAutoCompleteSearch'

import { colors } from '@globals/themes/defaultTheme'
import {
  FederatedSearchRecipientsParams,
  FederatedSearchRecipientsResults,
  SendTargetType,
  SendTo,
  TeamDisplayData,
  TenantMemberDisplayData,
  TenantMemberOrTeamDisplayData,
} from '@kudos/http-client'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TeamAvatar from '@shared/avatar/TeamAvatar'
import { isTenantMemberDisplayData } from '@shared/avatar/useAvatarConfig'
import UserAvatar from '@shared/avatar/UserAvatar'
import { fullNameFromParts } from '@shared/utils/nameFormatting'
import { t } from '@transifex/native'
import { T } from '@transifex/react'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  Control,
  Controller,
  FieldName,
  FieldPathValue,
  FieldValues,
  Path,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import ErrorText from '../ErrorText'
import { generateTeamName, getTeamIcon } from '../teamsAutoComplete/utils'
import useSearchTenantMembersAndTeams from './useSearchTenantMembersAndTeams'

const getAvatar = (member: TenantMemberOrTeamDisplayData) => {
  const isTenantMember = isTenantMemberDisplayData(member)
  return !isTenantMember ? (
    <TeamAvatar avatarBoxStyle={{ display: 'flex' }} size={25} />
  ) : (
    <UserAvatar
      size={25}
      firstName={member.firstName}
      lastName={member.lastName}
      media={member.avatarUrls}
      avatarBoxStyle={{ display: 'flex' }}
    />
  )
}

const renderChip = (
  option: AutoCompleteOption<TenantMemberOrTeamDisplayData>,
  props: AutoCompleteRenderChipProps,
  showAvatars?: boolean,
) => {
  const { key, ...otherProps } = props
  const isTeam = 'teamType' in option.value
  if (isTeam) {
    const team = option.value as TeamDisplayData
    return (
      <AutoCompleteChip
        key={key}
        {...otherProps}
        label={t('{teamName} ({activeMemberCount})', {
          teamName: generateTeamName(team.name, team.segmentType),
          activeMemberCount: team.activeMemberCount,
        })}
        icon={!showAvatars ? getTeamIcon(team.teamType) : undefined}
        avatar={showAvatars ? getAvatar(option.value) : undefined}
      />
    )
  } else {
    const tenantMember = option.value as TenantMemberDisplayData
    return (
      <AutoCompleteChip
        key={key}
        {...otherProps}
        label={fullNameFromParts(tenantMember.firstName, tenantMember.lastName)}
        avatar={showAvatars ? getAvatar(tenantMember) : undefined}
      />
    )
  }
}

const renderOption = (props: object, option: AutoCompleteOption<TenantMemberOrTeamDisplayData>) => {
  const isTeam = 'teamType' in option.value

  return (
    <Stack
      {...props}
      sx={{
        '& svg': {
          color: colors.grayDark,
        },
      }}
      key={option.id}
      direction="row"
      useFlexGap
      gap={1}>
      {isTeam ? getTeamIcon((option.value as TeamDisplayData).teamType) : <PersonOutlineIcon />}
      {isTeam ? (
        <Typography>
          {generateTeamName(
            (option.value as TeamDisplayData).name,
            (option.value as TeamDisplayData).segmentType,
          )}
        </Typography>
      ) : (
        <Typography>
          {fullNameFromParts(
            (option.value as TenantMemberDisplayData).firstName,
            (option.value as TenantMemberDisplayData).lastName,
          )}
        </Typography>
      )}
      {isTeam && (option.value as TeamDisplayData).activeMemberCount && (
        <Typography variant="caption">
          <T
            _str="{activeMemberCount, plural, one {{activeMemberCount} user} other {{activeMemberCount} users}}"
            activeMemberCount={(option.value as TeamDisplayData).activeMemberCount}
          />
        </Typography>
      )}
    </Stack>
  )
}

export type TenantMembersAndTeamsSearchFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  label?: ReactNode
  searchFn: (params: FederatedSearchRecipientsParams) => Promise<FederatedSearchRecipientsResults>
  delegatorId?: string
  showAvatars?: boolean
  disabled?: boolean
  zIndexPlacement?: AutoCompleteResultsZIndexPlacement
}

const TenantMembersAndTeamsSearchField = <T extends FieldValues>({
  name,
  control,
  label,
  searchFn,
  delegatorId,
  showAvatars,
  disabled,
  zIndexPlacement,
}: TenantMembersAndTeamsSearchFieldProps<T>) => {
  const [searchText, setSearchText] = useState<string>('')
  const { setValue } = useFormContext()
  const [options, setOptions] = useState<AutoCompleteOption<TenantMemberOrTeamDisplayData>[]>([])

  const selectedMembers = useWatch({ control, name })
  const selectedMembersIds = useMemo(
    () => selectedMembers?.map(({ id }: { id: string }) => id),
    [selectedMembers],
  )

  const { searchedTenantMembersAndTeams, isFetching } = useSearchTenantMembersAndTeams(
    searchText,
    selectedMembersIds,
    searchFn,
    delegatorId ?? undefined,
  )

  const onSelectedOptionsChanged = (
    options: AutoCompleteOption<TenantMemberOrTeamDisplayData>[],
  ) => {
    const selectedOptions: FieldPathValue<FieldValues, FieldName<FieldValues>> = options.map(
      option => option.value,
    )
    setValue(name, selectedOptions, { shouldValidate: true, shouldDirty: true })
  }

  useEffect(() => {
    setOptions(
      searchedTenantMembersAndTeams?.map(
        (member: TenantMemberOrTeamDisplayData) =>
          ({
            avatar: showAvatars ? getAvatar(member) : undefined,
            icon: 'teamType' in member && !showAvatars ? getTeamIcon(member.teamType) : undefined,
            value: member,
            id: member.id,
          }) as AutoCompleteOption<TenantMemberOrTeamDisplayData>,
      ) || [],
    )
  }, [searchedTenantMembersAndTeams])

  return (
    <Stack>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => (
          <>
            <StandardAutoCompleteSearch<TenantMemberOrTeamDisplayData>
              searchText={searchText}
              value={selectedMembers?.map((member: TenantMemberOrTeamDisplayData | SendTo) => {
                // TODO: remove this once BE is merged. This is to ensure backwards compatility was the BE will
                // change this logic to only return TenantMemberAndTeamDisplayData data.
                if (
                  'type' in member &&
                  member.name &&
                  member.type === SendTargetType.TENANT_MEMBER
                ) {
                  return {
                    id: member.id,
                    value: {
                      id: member.id,
                      firstName: member.name.split(' ')[0],
                      lastName: member.name.split(' ')[1],
                    } as TenantMemberOrTeamDisplayData,
                  }
                }
                return {
                  value: member,
                  id: member.id,
                }
              })}
              options={options}
              onSearchTextChanged={setSearchText}
              onSelectedOptionsChanged={onSelectedOptionsChanged}
              placeholder={t('Search users or teams...')}
              renderChip={(option, props) => renderChip(option, props, showAvatars)}
              label={label}
              renderOption={renderOption}
              groupBy={option => ('teamType' in option.value ? t('Teams') : t('Users'))}
              disabled={disabled}
              zIndexPlacement={zIndexPlacement}
              loading={isFetching}
              hideDropdown={true}
            />
            {!!error && <ErrorText>{error.message}</ErrorText>}
          </>
        )}
      />
    </Stack>
  )
}

export default TenantMembersAndTeamsSearchField
